import type { Dispatch, FC, SetStateAction } from "react"
import React, { useEffect, useMemo, useState } from "react"
import { Stack, Text, Button, Spinner } from "@chakra-ui/react"
import { useFormikContext } from "formik"
import invariant from "tiny-invariant"

import type {
  ActionsSecurityCheck,
  ActionsSecurityCheckQueryVariables,
  TransactionSimulationV2,
  ActionsSecurityCheckMetadata,
  ActionsSecurityCheckQuery,
  Governor,
  GovernorContract,
  Token,
  GovernorParameters,
  ExecutableCallInput,
} from "query/graphql"
import { ExecutableCallType, ActionsSecurityCheckDocument } from "query/graphql"
import type { CreateProposalFormValues } from "createProposal/types/form"
import { StepName } from "createProposal/types/form"
import { useMe } from "user/providers/MeProvider"
import { fetcher } from "common/helpers/fetcher"
import { useCreateProposal } from "createProposal/providers/CreateProposalProvider"
import CardContainer from "common/components/CardContainer"
import ProposalImpactDetails from "proposal/components/simulation/ProposalImpactDetails"

type CreateProposalImpactOverviewProps = {
  governance: Pick<Governor, "chainId" | "id"> & {
    contracts: {
      governor: Pick<GovernorContract, "address" | "type">
    }
    parameters: Pick<GovernorParameters, "proposalThreshold">
    token: Token
  }
  currentStep?: StepName

  proposalSecurityCheck: ActionsSecurityCheck | null
  setProposalSecurityCheck: Dispatch<
    React.SetStateAction<ActionsSecurityCheck | null>
  >
  triggerRunSecurityChecks: number
  setSecurityChecksRan: Dispatch<SetStateAction<boolean>>
  setDisplaySimulationModal: Dispatch<SetStateAction<boolean>>
}

const CreateProposalImpactOverview: FC<CreateProposalImpactOverviewProps> = ({
  currentStep,
  proposalSecurityCheck,
  setProposalSecurityCheck,
  triggerRunSecurityChecks,
  setSecurityChecksRan,
  setDisplaySimulationModal,
}) => {
  const me = useMe()

  const { isValid, values } = useFormikContext<CreateProposalFormValues>()
  const { governanceId } = useCreateProposal()

  const shouldDisplayImpactOverview = useMemo(() => {
    const isActionsEmpty =
      values?.actions?.every(
        (action) => action.recipe === ExecutableCallType.Empty,
      ) ?? true

    return !isActionsEmpty && isValid
  }, [values, isValid])

  const someActionIsIncomplete = useMemo(() => {
    const actionIsIncomplete =
      values?.actions?.some((action) => {
        const encoded = action.encodeForBlockchain()

        if (!encoded) return true

        return action.recipe === ExecutableCallType.Empty
      }) ?? true

    return actionIsIncomplete
  }, [values])

  const [isLoading, setIsLoading] = useState(false)
  const [metadata, setMetadata] = useState<ActionsSecurityCheckMetadata | null>(
    null,
  )
  const [simulations, setSimulations] = useState<
    TransactionSimulationV2[] | null
  >(null)

  const isActive = currentStep === StepName.IMPACT || !Boolean(currentStep)

  // Reset state when shouldDisplayImpactOverview is false or someActionIsIncomplete is true
  useEffect(() => {
    if (!shouldDisplayImpactOverview || someActionIsIncomplete) {
      setMetadata(null)
      setSimulations(null)
      setProposalSecurityCheck(null)
      setIsLoading(false)
      setSecurityChecksRan(false)
      setDisplaySimulationModal(false)
    }
  }, [
    someActionIsIncomplete,
    shouldDisplayImpactOverview,
    setProposalSecurityCheck,
    setSecurityChecksRan,
    setDisplaySimulationModal,
  ])

  useEffect(() => {
    if (proposalSecurityCheck) {
      setMetadata(proposalSecurityCheck.metadata)
      setSimulations(proposalSecurityCheck.simulations)
    } else {
      setMetadata(null)
      setSimulations(null)
      setProposalSecurityCheck(null)
      setIsLoading(false)
      setSecurityChecksRan(false)
      setDisplaySimulationModal(false)
    }
  }, [
    proposalSecurityCheck,
    setProposalSecurityCheck,
    setSecurityChecksRan,
    setDisplaySimulationModal,
  ])

  useEffect(() => {
    setMetadata(null)
    setSimulations(null)
    setProposalSecurityCheck(null)
    setIsLoading(false)
    setSecurityChecksRan(false)
    setDisplaySimulationModal(false)
  }, [
    values.actions,
    setProposalSecurityCheck,
    setSecurityChecksRan,
    setDisplaySimulationModal,
  ])

  // retrieve data
  const handleRunProposalSecurityChecks = async () => {
    setIsLoading(true)
    const { actions } = values

    invariant(actions, "`actions` must be defined")

    // Fixes a bug where a [EmptyAction, TransferAction] causes two executable calls instead of one
    const actionsToEncode =
      actions.length === 1
        ? actions
        : actions.filter((action) => action.recipe !== ExecutableCallType.Empty)

    const encodedActions = actionsToEncode.reduce((result, action) => {
      const encoded = action.encodeForTallyApi()

      if (!encoded) return result

      if (Array.isArray(encoded)) return [...result, ...encoded]

      return [...result, encoded]
    }, [] as ExecutableCallInput[])

    const performSecurityCheck = async (
      actions: ExecutableCallInput[] | undefined,
    ) => {
      if (!actions || !me) return

      return fetcher.gql<
        ActionsSecurityCheckQuery,
        ActionsSecurityCheckQueryVariables
      >({
        query: ActionsSecurityCheckDocument,
        variables: {
          input: {
            executableCalls: actions,
            governorId: governanceId,
            value: "0",
            proposer: me.id,
          },
        },
      })
    }

    const result = await performSecurityCheck(encodedActions)

    try {
      if (result) setProposalSecurityCheck(result.actionsSecurityCheck)
      else {
        console.error("Something went wrong getting security checks.")
      }
    } catch (err) {
    } finally {
      setIsLoading(false)

      setSecurityChecksRan(true)
      setDisplaySimulationModal(false)
    }
  }

  useEffect(() => {
    if (triggerRunSecurityChecks !== 0) handleRunProposalSecurityChecks()

    // We don't want this to re run because of handleRunProposalSecurityChecks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerRunSecurityChecks])

  // this check is down here to not interfere with hooks
  if (!isActive || !me || !values.actions) return null

  return proposalSecurityCheck &&
    shouldDisplayImpactOverview &&
    !someActionIsIncomplete ? (
    <ProposalImpactDetails
      simulations={simulations}
      threatAnalysis={metadata?.threatAnalysis ?? null}
      values={values}
    />
  ) : (
    <>
      <CardContainer mt={0} p={0} pt={{ base: 6, sm: 2 }} width="100%">
        <Stack p={4} spacing={5}>
          <Text>
            {!shouldDisplayImpactOverview || someActionIsIncomplete
              ? !isValid || someActionIsIncomplete
                ? "Please complete all required fields before running a simulation"
                : "This proposal has no associated actions and will have no impact on the treasury or any associated smart contracts"
              : "Run simulations to see results"}
          </Text>
          <Stack
            direction="row"
            hidden={!shouldDisplayImpactOverview || someActionIsIncomplete}
            spacing={2}
          >
            <Button
              isDisabled={
                !shouldDisplayImpactOverview ||
                someActionIsIncomplete ||
                isLoading
              }
              onClick={() => {
                handleRunProposalSecurityChecks()
              }}
            >
              {isLoading ? (
                <>
                  <Spinner mr={2} size="sm" />
                  Simulating...
                </>
              ) : (
                "Simulate execution"
              )}
            </Button>
          </Stack>
        </Stack>
      </CardContainer>
    </>
  )
}

export default CreateProposalImpactOverview
